
import { FC, useCallback, useContext, useMemo, useState } from 'react';

import DefaultClient from '../../../api/DefaultClient';
import { Authenticate, AuthenticateResponse } from '../../../api/dtos/dtos';
import AuthContext from '../../../contexts/auth/AuthContext';
import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import useServiceStackClient from '../../../hooks/api/useServiceStackClient';
import { useGetUser } from '../../../hooks/auth/useGetUser';
import { IHasChildren } from '../../common/types/IHasChildren';


interface IAuthProviderProps extends IHasChildren {
    initialValue?: AuthenticateResponse;
    refreshInterval?: number;
}

const AuthProvider: FC<IAuthProviderProps> = ({
    initialValue = null,
    refreshInterval = 600000,
    children
}) => {

    //
    // -- Hooks --
    const client = useServiceStackClient();

    const { species: { species } } = useContext(SpeciesContext);

    //
    // -- States --
    const [isLogin, setLogin] = useState<boolean>(false);
    const [loginMessage, setLoginMessage] = useState<string | undefined>(undefined);

    //
    // -- Requests --
    const authUser = useGetUser({ species: species }, {
        revalidateOnFocus: true, 
        errorRetryCount: 0, 
        refreshInterval: refreshInterval, 
        fallbackData: initialValue
    });

    /* - Authenticate -
     * 
     * Log user in to site. This will return a "refresh" and "access" token 
     * stored in SS cookies.
     * 
     * The login attempt will also try and get the current species referrenced
     * in the URL. This is to try and populate the user's session (in the backend)
     * with the user's roles. Currently this does not get used for anything but 
     * the intentions are to redesign the user-role system.
     */
    const authenticate = useCallback(async (username: string, password: string) => {
        setLoginMessage(undefined);

        try {
            // TODO: Loading
            const request = new Authenticate({
                provider: 'credentials',
                userName: username,
                password: password,
                rememberMe: true
            });
            
            const loginClient = DefaultClient(client.baseUrl, client.basePath ?? undefined);
            loginClient.enableAutoRefreshToken = false;

            await loginClient.post(request, { species: species });

            await authUser.refresh();

            return true;

        } catch (error: any) {
            setLoginMessage(error?.responseStatus?.message);
            return false;
        }
    }, [species, client, authUser.refresh, setLoginMessage]);

    /* - Logout -
     * 
     * Logs the user out of their current session. This includes clearing all
     * auth session cookies and generating a new unauthenticated session for 
     * the user.
     */
    const logout = useCallback(async () => {
        try {
            const request = new Authenticate({
                provider: 'logout',
                rememberMe: true
            });

            await client.post(request);
        } finally {
            await authUser.refresh();
        }

        return true;
    }, [client, authUser]);

    const context = useMemo(() => {
        return {
            ...authUser,
            user: authUser.data?.user ?? null,
            isLogin: isLogin,
            loginMessage: loginMessage,
            openLogin: () => setLogin(true),
            closeLogin: () => setLogin(false),
            authenticate,
            logout
        }
    }, [isLogin, authenticate, authUser, loginMessage]);

    /* - Validation -
     * 
     * Revalidates the user value when checking the user's current auth
     * status.
     */

    if (context.loading) {
        return null
    }

    return (
        <AuthContext.Provider value={context}>
            {children}
        </AuthContext.Provider>
    )
}

export default AuthProvider